import PendingBadge from "@/components/pending-badge"
import { TinyText } from "@/components/tiny-text"
import {
  ErrorFlagState,
  Input as ShadInput,
  InputProps as ShadInputProps,
} from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Icon } from "@/icons/icon"
import { isFilled } from "@/ipqc/lib/utils"
import { ParamGradeLabel } from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { cn } from "@/lib/utils"
import { ChangeEvent, HTMLAttributes, useEffect, useState } from "react"

export interface MultiReadingItem {
  id: string
  helpText?: string
  error?: string
  indicator?: ParamGradeLabel
  status: FormParamStatus
  flagState?: ErrorFlagState
  overtime: string
}

export type MultiReadingProps = {
  id?: string
  className?: string
  label: string
  labelHelpText?: string
  type?: "text" | "number"
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"]
  inputs: MultiReadingItem[]
  value?: string[]
  resampledValue?: string[]
  disabled?: boolean
  readOnly?: boolean
  showNaForUnresolved?: boolean
  direction?: "row" | "column"
  onClickErrorFlag?: (value: string[], index: number) => void
  onClickSubmittedErrorFlag?: (index: number) => void
  onClickSubmittedResampleFlag?: (index: number) => void
  onChange?: (value: string[], index: number) => void
  onFocus?: (value: string[], index: number) => void
  onClickSubmit?: (value: string[], index: number) => void
}

export default function MultiReading({
  id,
  className,
  label,
  value,
  resampledValue,
  type,
  inputMode,
  labelHelpText,
  inputs,
  disabled,
  showNaForUnresolved,
  direction = "row",
  onClickErrorFlag,
  onClickSubmittedErrorFlag,
  onClickSubmittedResampleFlag,
  onChange,
  onClickSubmit,
  onFocus,
  ...props
}: MultiReadingProps) {
  const [localValue, setLocalValue] = useState<string[]>([])

  function isDisabled(status?: FormParamStatus) {
    if (disabled) {
      return true
    }

    if (!status) {
      return false
    }

    const disabledStatuses: FormParamStatus[] = [
      FormParamStatus.NOT_APPLICABLE,
      FormParamStatus.DISABLED,
      FormParamStatus.BREAK_DOWN,
      FormParamStatus.CHANGE_OVER,
      FormParamStatus.POWER_FAILURE,
      FormParamStatus.OTHER,
    ]

    if (disabledStatuses.includes(status)) {
      return true
    }

    return false
  }

  function showPreview(status?: FormParamStatus) {
    if (!status || status === FormParamStatus.DRAFT) {
      return false
    }
    return true
  }

  function getSubmittedBadge(__value?: any, status?: FormParamStatus) {
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      showNaForUnresolved
    ) {
      return "NA"
    }

    return "PENDING"
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>, index: number) {
    const newValue = [...localValue]
    newValue[index] = e.target.value
    setLocalValue(newValue)

    if (onChange) {
      onChange(newValue, index)
    }
  }

  function handleFocus(index: number) {
    if (onFocus) {
      onFocus(localValue, index)
    }
  }

  function handleClickErrorFlag(index: number) {
    if (onClickErrorFlag) {
      onClickErrorFlag(localValue, index)
    }
  }

  function handleCLickSubmit(index: number) {
    if (onClickSubmit) {
      onClickSubmit(localValue, index)
    }
  }

  function handleClickSubmittedErrorFlag(index: number) {
    if (onClickSubmittedErrorFlag) {
      onClickSubmittedErrorFlag(index)
    }
  }

  function handleClickSubmittedResampleFlag(index: number) {
    if (onClickSubmittedResampleFlag) {
      onClickSubmittedResampleFlag(index)
    }
  }

  function getBadgeStyles(status?: FormParamStatus) {
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      showNaForUnresolved
    ) {
      return "text-button-black border-black"
    }

    return ""
  }

  function shouldShowValue(
    status?: FormParamStatus,
    valueText?: string,
    resampledValueText?: string
  ) {
    if (status === FormParamStatus.NOT_APPLICABLE) {
      return false
    }

    return isFilled(resampledValueText) || isFilled(valueText)
  }

  useEffect(() => {
    setLocalValue(value || [])
  }, [value])

  return (
    <div
      id={id}
      className={cn(
        "flex gap-4",
        direction === "row" ? "justify-between" : "flex-col gap-1",
        className
      )}
    >
      <div className="basis-2/5">
        <Label className="text-sm font-medium">{label}</Label>
        {labelHelpText && (
          <p className="mt-1.5 text-xs text-label-text">{labelHelpText}</p>
        )}
      </div>

      <div className="grid grid-cols-2 basis-3/5 gap-1 gap-y-2">
        {inputs.map((input, index) => {
          if (showPreview(input.status)) {
            const curr = value?.at(index)
            const currResampled = resampledValue?.at(index)

            return (
              <div className="flex flex-col justify-evenly" key={input.id}>
                <div className="flex items-center gap-3">
                  {shouldShowValue(input.status, curr, currResampled) ? (
                    <>
                      <span className="font-medium">
                        {isFilled(currResampled) ? currResampled : curr}
                      </span>
                      {input.flagState === ErrorFlagState.Filled && (
                        <button
                          className="hover:cursor-pointer"
                          type="button"
                          onClick={() => handleClickSubmittedErrorFlag(index)}
                        >
                          <Icon name="RedFlag" className="h-4 w-auto" />
                        </button>
                      )}

                      {input.flagState === ErrorFlagState.Resample && (
                        <button
                          className="hover:cursor-pointer"
                          type="button"
                          onClick={() =>
                            handleClickSubmittedResampleFlag(index)
                          }
                        >
                          <Icon name="ResampleBadge" className="h-6 w-auto" />
                        </button>
                      )}
                    </>
                  ) : (
                    <PendingBadge
                      className={getBadgeStyles(input.status)}
                      text={getSubmittedBadge(curr, input.status)}
                    />
                  )}

                  {!!input.indicator && (
                    <div
                      className={cn(
                        "indicator h-1.5 w-1.5 rounded-full",
                        input.indicator === ParamGradeLabel.Green &&
                        "bg-success",
                        input.indicator === ParamGradeLabel.Orange &&
                        "bg-resample",
                        input.indicator === ParamGradeLabel.Yellow &&
                        "bg-graph-yellow"
                      )}
                    ></div>
                  )}
                </div>

                <div className="flex flex-col">
                  {input.helpText && <TinyText text={input.helpText} />}
                  {input.overtime && (
                    <TinyText className="text-error" text={input.overtime} />
                  )}
                </div>
              </div>
            )
          }

          return (
            <Input
              key={input.id}
              type={type}
              inputMode={inputMode}
              value={value?.at(index)}
              error={input.error}
              indicator={input.indicator}
              inputHelpText={input.helpText}
              disabled={isDisabled(input.status)}
              errorFlagState={input.flagState}
              overtime={input.overtime}
              {...props}
              onClickErrorFlag={() => handleClickErrorFlag(index)}
              onChange={(e) => handleChange(e, index)}
              onFocus={() => handleFocus(index)}
              onClickSubmit={() => handleCLickSubmit(index)}
            />
          )
        })}
      </div>
    </div>
  )
}

type InputProps = ShadInputProps & {
  indicator?: ParamGradeLabel
  inputHelpText?: string
  errorFlagState?: ErrorFlagState
  overtime?: string
  onClickSubmit?: (value: string) => void
}

function Input({
  className,
  type,
  value,
  error,
  indicator,
  inputHelpText,
  disabled,
  errorFlagState,
  overtime,
  onClickErrorFlag,
  onChange,
  onClickSubmit,
  ...props
}: InputProps) {
  const [localValue, setLocalValue] = useState(value)

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    setLocalValue(e.target.value)

    if (onChange) {
      onChange(e)
    }
  }

  function handleCLickSubmit() {
    if (onClickSubmit) {
      onClickSubmit(`${localValue}`)
    }
  }

  function isSubmitBtnShown() {
    return false
  }

  function isCheckedShown() {
    return !error && !!value && !errorFlagState
  }

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <div>
      <div className="relative">
        <ShadInput
          className={cn(className)}
          type={type}
          value={localValue}
          error={error}
          indicator={indicator}
          disabled={disabled}
          errorFlagState={errorFlagState}
          onClickErrorFlag={onClickErrorFlag}
          onChange={handleChange}
          {...props}
        />

        {isSubmitBtnShown() && <SubmitButton onClick={handleCLickSubmit} />}
        {isCheckedShown() && (
          <Icon
            name="Checkbox"
            className="absolute top-1/2 right-3 -translate-y-1/2"
          />
        )}
      </div>

      <div className="flex flex-col">
        {inputHelpText && <TinyText text={inputHelpText} />}
        {overtime && <TinyText className="text-error" text={overtime} />}
      </div>
    </div>
  )
}

type SubmitButtonProps = {
  onClick?: () => void
}

function SubmitButton({ onClick }: SubmitButtonProps) {
  return (
    <button
      className={cn(
        `
          bg-blue-fill absolute top-1/2 right-[1px]
          flex h-[46px] w-9 -translate-y-1/2 items-center
          justify-center rounded-tr-[7px] rounded-br-[7px] hover:cursor-pointer
        `
      )}
      onClick={onClick}
    >
      <span className="sr-only">Submit value</span>
      <Icon name="ArrowRight" className="text-blue size-2.5" />
    </button>
  )
}
